import { Injectable } from '@angular/core';
import {
  ApprovedHousingComplex,
  CampusWardSessionInformation,
  LocalWard,
  CampusWard,
  CampusStake,
  AcademicSession,
  WardMeeting,
  CampusUnit,
  ChurchUnit,
  WardGroupType,
  WardGroup,
  StudentListing,
  HomeWardWithStudents,
  MemberInfo,
  CampusUnitsFaqSection,
  HomeWardWithCheckAbleStudents,
  CheckableStudentListing
} from '../models';
import { ApprovedHousingSessionInformation } from '../index';
import { SortableChurchUnit } from '../models/ecclesiastical/sortable-church-unit.model';

@Injectable()
export class SortService {
  constructor() { }

  /**
   * Sorts two ward meetings, based on their order.
   */
  sortWardMeetingsAsc(meeting1: WardMeeting, meeting2: WardMeeting) {
    if (meeting1.sortOrder !== undefined && meeting2.sortOrder !== undefined) {
      return meeting1.sortOrder - meeting2.sortOrder;
    }

    if (!meeting1.sortOrder && !meeting2.sortOrder) {
      return meeting1.meetingType.description
        .toUpperCase()
        .localeCompare(meeting2.meetingType.description.toUpperCase());
    }

    return 1;
  }

  /**
   * Compares two housing complexes and sorts them ascending.
   */
  sortHousingComplexesAsc(
    complex1: ApprovedHousingComplex,
    complex2: ApprovedHousingComplex
  ) {
    return complex1.name
      .toUpperCase()
      .localeCompare(complex2.name.toUpperCase());
  }

  /**
   * Compares two housing complex/semester informations.
   */
  sortApprovedHousingSessionInformationAsc(
    info1: ApprovedHousingSessionInformation,
    info2: ApprovedHousingSessionInformation
  ): number {
    return info1.housingComplex
      .toUpperCase()
      .localeCompare(info2.housingComplex.toUpperCase());
  }

  /**
   * Sorts the church units, ascending.
   */
  sortChurchUnitsAsc(
    unit1: SortableChurchUnit,
    unit2: SortableChurchUnit
  ): number {
    return sortChurchUnitAsc(
      unit1.sortName,
      unit1.sortNumber,
      unit2.sortName,
      unit2.sortNumber
    );
  }

  /**
   * Compares two ward/semester informations.
   */
  sortCampusWardSessionInformationsAsc(
    info1: CampusWardSessionInformation,
    info2: CampusWardSessionInformation
  ): number {
    return sortChurchUnitAsc(
      info1.campusWard.sortName,
      info1.campusWard.sortNumber,
      info2.campusWard.sortName,
      info2.campusWard.sortNumber
    );
  }

  /**
   * Compares two local wards based on their name.
   */
  sortLocalWardsAsc(ward1: LocalWard, ward2: LocalWard): number {
    return sortChurchUnitAsc(
      ward1.localUnit.sortName,
      ward1.localUnit.sortNumber,
      ward2.localUnit.sortName,
      ward2.localUnit.sortNumber
    );
  }

  sortFaqSections(s1: CampusUnitsFaqSection, s2: CampusUnitsFaqSection) {
    return s1.name.toUpperCase().localeCompare(s2.name.toUpperCase());
  }

  /**
   * Compares two academic sessions based on their begin dates.  If I need to do this again, refactor it so that the
   * method can be shared.
   */
  sortAcademicSessionsAsc(
    session1: AcademicSession,
    session2: AcademicSession
  ): number {
    if (session1.beginDate > session2.beginDate) {
      return 1;
    }

    if (session1.beginDate === session2.beginDate) {
      return 0;
    }

    return -1;
  }

  /**
   * Sorts two student listings alphabetically, lastname, then first name.
   */
  sortStudentListingsAsc(student1: StudentListing, student2: StudentListing) {
    let compared = student1.lastName
      .toUpperCase()
      .localeCompare(student2.lastName.toUpperCase());
    if (compared === 0) {
      compared = student1.firstName
        .toUpperCase()
        .localeCompare(student2.firstName.toUpperCase());
    }

    return compared;
  }

  /**
   * Sorts two checkable student listings alphabetically, lastname, then first name.
   */
  sortCheckableStudentListingsAsc(
    student1: CheckableStudentListing,
    student2: CheckableStudentListing
  ) {
    let compared = student1.lastName
      .toUpperCase()
      .localeCompare(student2.lastName.toUpperCase());
    if (compared === 0) {
      compared = student1.firstName
        .toUpperCase()
        .localeCompare(student2.firstName.toUpperCase());
    }

    return compared;
  }

  /**
   * Sort 2 members or the ward directory.
   */
  sortWardDirectoryByLastName(member1: MemberInfo, member2: MemberInfo) {
    let compared = member1.lastName
      .toUpperCase()
      .localeCompare(member2.lastName.toUpperCase());
    if (compared === 0) {
      compared = member1.firstName
        .toUpperCase()
        .localeCompare(member2.firstName.toUpperCase());
    }
    return compared;
  }

  sortWardDirectoryByAddress(member1: MemberInfo, member2: MemberInfo) {
    return member1.address
      .toUpperCase()
      .localeCompare(member2.address.toUpperCase());
  }

  /**
   * Sorts the ward group types ascending, based on name.
   */
  sortWardGroupTypesAsc(type1: WardGroupType, type2: WardGroupType): number {
    const test = type1.name.toUpperCase().localeCompare(type2.name.toUpperCase());
    return type1.name.toUpperCase().localeCompare(type2.name.toUpperCase());
  }

  /**
   * Sorts the ward groups ascending, based on name.  If there's a number in the name and the two other parts
   * of the name are the same, we'll sort based on the number.
   */
  sortWardGroupsAsc(group1: WardGroup, group2: WardGroup): number {
    // often they'll have numbers in their groups, so we'll try to find the number and if all the non-number
    // stuff is the same, we'll sort by the numbers
    // /^(\D*)(\d*)(.*)$/g
    const regex1 = /^(\D*)(\d*)$/g;
    const regex2 = /^(\D*)(\d*)$/g;
    const group1Match = regex1.exec(group1.name);
    const group2Match = regex2.exec(group2.name);

    // in case regrex doesnt match
    if (group1Match !== null && group2Match !== null) {
      if (group1Match.length === 3 && group2Match.length === 3) {
        let compared = group1Match[1]
          .toUpperCase()
          .localeCompare(group2Match[1].toUpperCase());
        if (compared === 0) {
          compared = +group1Match[2] - +group2Match[2];
        }
        return compared;
      }
    }
    return group1.name.toUpperCase().localeCompare(group2.name.toUpperCase());
  }

  /**
   * Sorts the home ward with students by the ward.
   */
  sortHomeWardWithStudentsAsc(
    one: HomeWardWithStudents,
    two: HomeWardWithStudents
  ) {
    return sortChurchUnitAsc(
      one.homeWard.sortName,
      one.homeWard.sortNumber,
      two.homeWard.sortName,
      two.homeWard.sortNumber
    );
  }
  /**
   * Sorts the home ward with checkable students by the ward.
   */
  sortHomeWardWithCheckableStudentsAsc(
    one: HomeWardWithCheckAbleStudents,
    two: HomeWardWithCheckAbleStudents
  ) {
    return sortChurchUnitAsc(
      one.homeWard.sortName,
      one.homeWard.sortNumber,
      two.homeWard.sortName,
      two.homeWard.sortNumber
    );
  }
}

/**
 * Compares two units' sort information to get them in the correct order.
 */
function sortChurchUnitAsc(
  sortName1: string,
  sortNumber1: number,
  sortName2: string,
  sortNumber2: number
): number {
  let sorted: number = sortName1
    .toUpperCase()
    .localeCompare(sortName2.toUpperCase());
  if (sorted === 0) {
    sorted = sortNumber1 - sortNumber2;
  }

  return sorted;
}
